import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ErasureRequestDTO, ErasureStatus, EXPECTED_CONTEXTS } from '@today/types'
import type { ErasureDetailView, ErasureRequestPanelViewProps, Translate } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { complianceCapabilities } from '@/auth/compliance-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { COMPLIANCE_NAMESPACE } from '@/i18n'
import { complianceService } from '@/services/compliance.service'
import { complianceError } from './compliance-errors'
import { formatDate, openDownload, POLLED_ERASURE_STATUSES, REFRESH_MS } from './compliance-ui'
import { ERASURES_KEY } from './useErasureList'

const CANCELLABLE: ReadonlySet<ErasureStatus> = new Set([
	ErasureStatus.PREVIEW_PENDING,
	ErasureStatus.AWAITING_CONFIRMATION,
])

type Transition = (requestId: string) => Promise<ErasureRequestDTO>

/** "n of m": the distinct contexts that reported the current step, of those expected (REQ-203). */
const progressOf = (request: ErasureRequestDTO, t: Translate) => {
	const step = request.currentStep
	if (!step) return null
	const reported = new Set(request.steps.filter((s) => s.step === step).map((s) => s.context)).size
	return t(`panel.progress.${step}`, { n: reported, m: EXPECTED_CONTEXTS[step].length })
}

const toDetailView = (request: ErasureRequestDTO, t: Translate, language: string): ErasureDetailView => ({
	id: request.id,
	customerId: request.customerId,
	statusLabel: t(`erasureStatus.${request.status}`),
	basisLabel: t(`basis.${request.basis}`),
	graceDays: request.graceDays,
	requestedAt: formatDate(request.requestedAt, language),
	graceEndsAt: formatDate(request.graceEndsAt, language),
	progress: progressOf(request, t),
	preview: request.preview.length > 0 ? request.preview : null,
	certificateSha256: request.certificateSha256,
})

/** The panel's mutations: a status transition (confirm / cancel) and the certificate link. */
function useErasureActions(id: string, key: readonly unknown[], onError: (error: unknown) => void) {
	const queryClient = useQueryClient()
	const transition = useMutation({
		mutationFn: (action: Transition) => action(id),
		onSuccess: (updated) => {
			queryClient.setQueryData(key, updated)
			void queryClient.invalidateQueries({ queryKey: ERASURES_KEY })
		},
		onError,
	})
	const certificate = useMutation({
		mutationFn: () => complianceService.certificateLink(id),
		onSuccess: (link) => openDownload(link.url),
		onError,
	})
	return { transition: (action: Transition) => transition.mutate(action), certificate: () => certificate.mutate() }
}

/**
 * One erasure request's panel (TO-232 compliance-admin-ui REQ-203): refreshed
 * every 5 s while PREVIEW_PENDING or ERASING; confirm goes through a dialog;
 * each action is offered only in its statuses and with its permission.
 */
export function useErasureRequest(id: string, onClose: () => void): ErasureRequestPanelViewProps {
	const { t, i18n } = useTranslation(COMPLIANCE_NAMESPACE)
	const caps = complianceCapabilities(useViewerPermissions().permissions)
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [message, setMessage] = useState<string | null>(null)
	const key = [...ERASURES_KEY, 'detail', id]
	const detail = useQuery({
		queryKey: key,
		queryFn: () => complianceService.getErasure(id),
		refetchInterval: (query) =>
			query.state.data && POLLED_ERASURE_STATUSES.has(query.state.data.status) ? REFRESH_MS : false,
	})
	const actions = useErasureActions(id, key, (error) => setMessage(complianceError(error, t).message))
	const act = (run: () => void) => () => {
		setMessage(null)
		run()
	}
	const status = detail.data?.status
	const manage = caps.manageErasures
	return {
		t,
		request: detail.data ? toDetailView(detail.data, t, i18n.language) : null,
		message: message ?? (detail.isError ? complianceError(detail.error, t).message : null),
		confirmOpen,
		onConfirm: manage && status === ErasureStatus.AWAITING_CONFIRMATION ? () => setConfirmOpen(true) : undefined,
		onConfirmAccept: act(() => {
			setConfirmOpen(false)
			actions.transition(complianceService.confirmErasure)
		}),
		onConfirmDismiss: () => setConfirmOpen(false),
		onCancel:
			manage && status && CANCELLABLE.has(status)
				? act(() => actions.transition(complianceService.cancelErasure))
				: undefined,
		onDownloadCertificate: status === ErasureStatus.COMPLETED ? act(actions.certificate) : undefined,
		onClose,
	}
}
